'use client';
import React, { useEffect, useState } from 'react';
import { OrderHistory as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { convertToLocale } from '@lib/util/money';

const SharedRender = (Shared as any).render;

const STATUS_MAP: Record<string, 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'> = {
  pending: 'pending',
  processing: 'processing',
  shipped: 'shipped',
  delivered: 'delivered',
  cancelled: 'cancelled',
  completed: 'delivered',
  not_paid: 'pending',
  paid: 'processing',
  fulfilled: 'shipped',
};

function mapOrders(orders: any[]) {
  return orders.map((o: any) => {
    const currency = o.currency_code || 'usd';
    const fmt = (amount: number) =>
      convertToLocale({ amount: Number(amount) || 0, currency_code: currency });
    return {
      id: o.id,
      orderNumber: o.display_id ? `#${o.display_id}` : (o.id?.slice(0, 8) ?? ''),
      date: o.created_at ? new Date(o.created_at).toISOString().slice(0, 10) : '',
      status: STATUS_MAP[o.status] ?? 'pending',
      total: fmt(o.total),
      itemCount: (o.items ?? []).reduce((sum: number, it: any) => sum + (it.quantity ?? 1), 0),
      items: (o.items ?? []).map((it: any) => ({
        id: it.id,
        name: it.title ?? it.product_title ?? '',
        image: it.thumbnail,
        quantity: it.quantity ?? 1,
        price: fmt(it.unit_price),
      })),
    };
  });
}

/**
 * Storefront OrderHistory — prefers orders injected into Puck context by
 * the page (server-fetched via listOrders). Falls back to a client-side
 * fetch for pages that don't pass context, and to the shared MOCK in
 * editor preview.
 */
function OrderHistoryImpl(props: any) {
  const { context } = usePuckContext() as any;
  const [fallbackOrders, setFallbackOrders] = useState<any[] | null | undefined>(undefined);
  const customerId = context?.customer?.id;

  // 1. Page injected orders into context — use them.
  const contextOrders: any[] | undefined = context?.orders;
  if (contextOrders !== undefined) {
    return <SharedRender {...props} orders={mapOrders(contextOrders)} />;
  }

  // 2. Editor preview with sample data.
  if (context?.sampleOrders) {
    return <SharedRender {...props} orders={mapOrders(context.sampleOrders)} />;
  }

  // 3. Fallback: client-side fetch (for pages that haven't been migrated yet).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch('/api/customers/me/orders', { credentials: 'include' });
        if (!res.ok) {
          if (mounted) setFallbackOrders(undefined);
          return;
        }
        const data = await res.json();
        if (mounted) setFallbackOrders(data.orders || []);
      } catch {
        if (mounted) setFallbackOrders(undefined);
      }
    })();
    return () => { mounted = false; };
  }, [customerId]);

  if (fallbackOrders === undefined) {
    return <SharedRender {...props} orders={undefined} />;
  }
  if (fallbackOrders === null) return <></>;
  return <SharedRender {...props} orders={mapOrders(fallbackOrders)} />;
}

export const OrderHistory: any = {
  ...Shared,
  render: (props: any) => <OrderHistoryImpl {...props} />,
};
export default OrderHistory;
